import React, { useState } from "react";
import "./MessageTemplateModal.css";
import Input from "../../../../components/reusable-input/Input";
import CustomTimePicker from "../../../../components/custom-timepicker/CustomTimePicker";
import Switch from "../../../../components/custom-switch/Switch";
import { Activity } from "../../../../apis/activityApis";
import { toast } from "react-toastify";

interface MessageTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  metrics: Activity[];
  loading: boolean;
}

// const metrics = ["Steps Count", "Active Hours", "Sleep Hours"];
const conditionOptions = [
  { label: "Less Than", value: "Less Than" },
  { label: "Greater Than", value: "Greater Than" },
  { label: "Equal To", value: "Equal To" },
];

// Convert 12h (09:00 AM / 09:00 PM) → 24h (09:00 / 21:00)
const convertTo24Hour = (time12h: string): string => {
  if (!time12h) return "00:00";

  const [time, modifier] = time12h.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) {
    hours += 12;
  }

  if (modifier === "AM" && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

// Convert 24h (21:00) → 12h (09:00 PM)
const convertTo12Hour = (time24h: string): string => {
  if (!time24h) return "12:00 AM";

  let [hours, minutes] = time24h.split(":").map(Number);

  const modifier = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${modifier}`;
};

const MessageTemplateModal: React.FC<MessageTemplateModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  metrics,
  loading,
}) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [body, setBody] = useState(initialData?.desc || "");
  const [metric, setMetric] = useState(initialData?.metric || "");
  const [condition, setCondition] = useState(initialData?.condition || "");
  const [value, setValue] = useState(initialData?.value || "");
  const [timeType, setTimeType] = useState(initialData?.timeType || "Before");
  const [time, setTime] = useState(
    initialData?.time
      ? initialData.time.includes("AM") || initialData.time.includes("PM")
        ? initialData.time
        : convertTo12Hour(initialData.time)
      : "12:00 AM",
  );
  const [status, setStatus] = useState(initialData?.status ?? "Active");

  if (!open) return null;

  const handleSubmit = () => {
    if (!title || !body || !condition || !value || !time) {
      toast.error("Please fill all required fields");
      return;
    }
    onSubmit({
      title,
      body,
      metric,
      condition,
      value,
      timeType,
      time: convertTo24Hour(time),
      status,
    });
  };

  return (
    <div className="msgTemplateModal-overlay">
      <div className="msgTemplateModal-container">
        <h2 className="msgTemplateModal-heading">
          {initialData ? "Edit Template" : "Create Template"}
        </h2>
        <div className="msgTemplateModal-body">
          <div className="msgTemplateModal-field">
            <label>Message Title *</label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter Title"
              search={false}
            />
          </div>

          <div
            className="msgTemplateModal-field"
            style={{ marginBottom: "15px" }}
          >
            <label>Message Body *</label>
            <textarea
              className="msgTemplateModal-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter Message"
            />
          </div>

          <div className="msgTemplateModal-row">
            <div className="msgTemplateModal-field">
              <label>Metric *</label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="msgTemplateModal-select"
              >
                <option value="">Select</option>
                {metrics.map((activity) => (
                  <option key={activity.id} value={activity.id}>
                    {activity.activityName}
                  </option>
                ))}
              </select>
            </div>

            <div className="msgTemplateModal-field">
              <label>Condition *</label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                className="msgTemplateModal-select"
              >
                <option value="">Select</option>
                {conditionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="msgTemplateModal-field">
              <label>Value *</label>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter Value"
                className="msgTemplateModal-input"
              />
            </div>
          </div>

          <div className="msgTemplateModal-row time-row">
            <div className="msgTemplateModal-field">
              <label>Time *</label>
              <div className="time-options">
                <div className="msgTemplate-timeRadio">
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="After"
                      checked={timeType === "After"}
                      onChange={(e) => setTimeType(e.target.value)}
                    />
                    After
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      value="Before"
                      checked={timeType === "Before"}
                      onChange={(e) => setTimeType(e.target.value)}
                    />
                    Before
                  </label>
                </div>

                <div className="ampm-toggle">
                  <CustomTimePicker
                    value={time.split(" ")[0] || "12:00"}
                    onChange={(t) => {
                      const currentModifier = time.includes("PM") ? "PM" : "AM";
                      setTime(`${t} ${currentModifier}`);
                    }}
                    step={1}
                  />

                  <button
                    type="button"
                    className={`ampm-btn am ${time.includes("AM") ? "active" : ""}`}
                    onClick={() => setTime(time.split(" ")[0] + " AM")}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    className={`ampm-btn ${time.includes("PM") ? "active" : ""}`}
                    onClick={() => setTime(time.split(" ")[0] + " PM")}
                  >
                    PM
                  </button>
                </div>
              </div>
            </div>
          </div>

          {initialData && (
            <div className="msgTemplateModal-field">
              <Switch
                label={status}
                checked={status === "Active"}
                onChange={() =>
                  setStatus((prev: string) =>
                    prev === "Active" ? "Inactive" : "Active",
                  )
                }
              />
            </div>
          )}
        </div>

        <div className="msgTemplateModal-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="submit-btn" onClick={handleSubmit}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageTemplateModal;
